import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { X, ChevronLeft, ChevronRight, Check, Timer, Play, Trophy, Dumbbell } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Loader } from "../components/common";

export default function WorkoutPlayer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [workout, setWorkout] = useState(null);
  const [idx, setIdx] = useState(0);
  const [started, setStarted] = useState(false);
  const [logs, setLogs] = useState({});
  const [rest, setRest] = useState(0);
  const [showComplete, setShowComplete] = useState(false);
  const [survey, setSurvey] = useState({ difficulty: 4, energy: 4, pain: false, pain_location: "", confidence: 4, note: "" });
  const restRef = useRef(null);

  useEffect(() => { api.get(`/assignments/${id}`).then(({ data }) => setWorkout(data)).catch(() => navigate("/app")); }, [id]);

  useEffect(() => {
    if (rest > 0) { restRef.current = setTimeout(() => setRest(rest - 1), 1000); }
    else if (rest === 0 && restRef.current) { clearTimeout(restRef.current); restRef.current = null; }
    return () => clearTimeout(restRef.current);
  }, [rest]);

  if (!workout) return <div className="min-h-screen bg-[#0A0A0A]"><Loader /></div>;
  if (workout.is_rest) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-5">
        <div className="max-w-sm w-full text-center">
          <h1 className="font-heading text-4xl">Rest Day</h1>
          <p className="text-secondary mt-2">Your coach scheduled today as recovery. Rest up, hydrate, and come back strong.</p>
          <button data-testid="rest-back" onClick={() => navigate("/app")} className="w-full mt-6 py-3 bg-brand text-white font-bold uppercase tracking-wide rounded-xl hover:bg-brand-hover">Back to Dashboard</button>
        </div>
      </div>
    );
  }
  const exercises = workout.exercises || [];
  const ex = exercises[idx];
  const completedCount = Object.values(logs).filter((l) => l.done).length;
  const progress = Math.round((completedCount / exercises.length) * 100);

  const setLog = (exIdx, patch) => setLogs((l) => ({ ...l, [exIdx]: { ...l[exIdx], ...patch } }));

  const markDone = () => {
    setLog(idx, { done: true });
    if (ex.rest) setRest(ex.rest);
    toast.success(`${ex.name} done!`);
    if (idx < exercises.length - 1) setTimeout(() => setIdx(idx + 1), 400);
  };

  const finish = async () => {
    try {
      await api.post(`/assignments/${id}/complete`, { exercise_logs: logs, ...survey });
      toast.success("Workout logged!");
      navigate("/app");
    } catch (e) { toast.error("Could not save workout."); }
  };

  // ---- Pre-workout intro ----
  if (!started) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <div className="max-w-md mx-auto p-5">
          <button data-testid="player-close" onClick={() => navigate("/app")} className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center"><X className="w-5 h-5" /></button>
          <h1 className="font-heading text-5xl mt-6">{workout.workout_name}</h1>
          <p className="text-secondary mt-2">{workout.objective}</p>
          <div className="grid grid-cols-3 gap-3 mt-5">
            {[["Duration", `${workout.duration_min} min`], ["Difficulty", workout.difficulty], ["Exercises", exercises.length]].map(([l, v]) => (
              <div key={l} className="card-surface rounded-xl p-3 text-center"><p className="label-xs">{l}</p><p className="font-heading text-2xl mt-1">{v}</p></div>
            ))}
          </div>
          {workout.coach_intro && <div className="mt-5 card-surface rounded-xl p-4"><p className="label-xs mb-1">Coach Intro</p><p className="text-sm text-secondary italic">"{workout.coach_intro}"</p></div>}
          <div className="mt-4 space-y-3">
            <div><p className="label-xs mb-1">Warm-up</p><p className="text-sm text-secondary">{workout.warmup}</p></div>
            <div><p className="label-xs mb-1">Equipment</p><p className="text-sm text-secondary">{workout.equipment}</p></div>
            <div className="bg-warning/10 border border-warning/20 rounded-lg p-3"><p className="text-xs text-warning">⚠ {workout.safety_notes}</p></div>
          </div>
          <button data-testid="player-begin" onClick={() => setStarted(true)}
            className="w-full mt-6 py-4 bg-brand text-white font-bold uppercase tracking-wide rounded-xl glow-red hover:bg-brand-hover transition-all flex items-center justify-center gap-2">
            <Play className="w-5 h-5 fill-white" /> Begin Workout
          </button>
        </div>
      </div>
    );
  }

  // ---- Completion celebration + survey ----
  if (showComplete) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] max-w-md mx-auto p-5">
        <div className="text-center pt-8 animate-pop">
          <div className="w-24 h-24 mx-auto rounded-full bg-success/20 flex items-center justify-center glow-red"><Trophy className="w-12 h-12 text-success" /></div>
          <h1 className="font-heading text-5xl mt-5">Crushed It!</h1>
          <p className="text-secondary mt-1">You completed {completedCount} of {exercises.length} exercises.</p>
        </div>
        <div className="mt-8 space-y-5">
          <Rating label="How difficult was it?" value={survey.difficulty} onChange={(v) => setSurvey({ ...survey, difficulty: v })} testId="survey-difficulty" />
          <Rating label="How was your energy?" value={survey.energy} onChange={(v) => setSurvey({ ...survey, energy: v })} testId="survey-energy" />
          <Rating label="How confident did you feel?" value={survey.confidence} onChange={(v) => setSurvey({ ...survey, confidence: v })} testId="survey-confidence" />
          <div>
            <p className="label-xs mb-2">Did you experience pain?</p>
            <div className="flex gap-2">
              <button data-testid="survey-pain-no" onClick={() => setSurvey({ ...survey, pain: false })} className={`flex-1 py-2.5 rounded-lg border ${!survey.pain ? "border-success bg-success/10 text-success" : "border-border text-secondary"}`}>No pain</button>
              <button data-testid="survey-pain-yes" onClick={() => setSurvey({ ...survey, pain: true })} className={`flex-1 py-2.5 rounded-lg border ${survey.pain ? "border-brand bg-brand/10 text-brand" : "border-border text-secondary"}`}>Yes</button>
            </div>
            {survey.pain && <input data-testid="survey-pain-loc" placeholder="Where? (e.g. lower back)" value={survey.pain_location} onChange={(e) => setSurvey({ ...survey, pain_location: e.target.value })} className="w-full mt-2 bg-surface-elevated border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-brand" />}
          </div>
          <textarea data-testid="survey-note" placeholder="Send your coach a note (optional)" value={survey.note} onChange={(e) => setSurvey({ ...survey, note: e.target.value })} rows={2}
            className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-brand" />
          <button data-testid="survey-submit" onClick={finish} className="w-full py-4 bg-brand text-white font-bold uppercase tracking-wide rounded-xl hover:bg-brand-hover transition-all">
            Finish & Save
          </button>
        </div>
      </div>
    );
  }

  // ---- Exercise player ----
  const log = logs[idx] || {};
  return (
    <div className="min-h-screen bg-[#0A0A0A] max-w-md mx-auto flex flex-col">
      <div className="p-4 flex items-center gap-3 border-b border-border">
        <button data-testid="player-exit" onClick={() => navigate("/app")} className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center"><X className="w-4 h-4" /></button>
        <div className="flex-1">
          <div className="h-2 bg-white/10 rounded-full"><div className="h-full bg-brand rounded-full transition-all" style={{ width: `${progress}%` }} /></div>
        </div>
        <span className="font-num text-sm text-secondary">{idx + 1}/{exercises.length}</span>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <video key={ex.exercise_id} src={ex.video_url} poster={ex.thumbnail} controls playsInline className="w-full rounded-xl bg-black aspect-video" data-testid="exercise-video" />
        <h1 className="font-heading text-4xl mt-4">{ex.name}</h1>
        <div className="flex flex-wrap gap-2 mt-2">
          {ex.muscle_groups?.map((m) => <span key={m} className="text-xs px-2 py-0.5 rounded-full bg-white/5 text-secondary">{m}</span>)}
          <span className="text-xs px-2 py-0.5 rounded-full bg-white/5 text-secondary">{ex.equipment}</span>
        </div>

        <div className="grid grid-cols-3 gap-2 mt-4">
          {[["Sets", ex.sets], ["Reps", ex.reps || "—"], ["Rest", `${ex.rest}s`]].map(([l, v]) => (
            <div key={l} className="card-surface rounded-lg p-3 text-center"><p className="label-xs">{l}</p><p className="font-heading text-2xl mt-0.5">{v}</p></div>
          ))}
        </div>
        {(ex.rpe || ex.tempo || ex.weight) && (
          <div className="flex gap-3 mt-2 text-xs text-secondary">
            {ex.weight && <span>Weight: {ex.weight}</span>}{ex.tempo && <span>Tempo: {ex.tempo}</span>}{ex.rpe && <span>RPE: {ex.rpe}</span>}
          </div>
        )}

        {ex.coach_notes && <div className="mt-4 card-surface rounded-xl p-3 border-l-2 border-brand"><p className="label-xs mb-1">Coach Note</p><p className="text-sm text-secondary">{ex.coach_notes}</p></div>}

        <div className="mt-4">
          <p className="label-xs mb-2">Log your set</p>
          <div className="grid grid-cols-3 gap-2">
            <input data-testid="log-weight" placeholder="Weight" value={log.weight || ""} onChange={(e) => setLog(idx, { weight: e.target.value })} className="bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-brand" />
            <input data-testid="log-reps" placeholder="Reps" value={log.reps || ""} onChange={(e) => setLog(idx, { reps: e.target.value })} className="bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-brand" />
            <input data-testid="log-rpe" placeholder="RPE" value={log.rpe || ""} onChange={(e) => setLog(idx, { rpe: e.target.value })} className="bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-brand" />
          </div>
        </div>

        <details className="mt-4 card-surface rounded-xl p-3">
          <summary className="text-sm font-semibold cursor-pointer">How to perform</summary>
          <ol className="mt-2 space-y-1.5 text-sm text-secondary list-decimal list-inside">{ex.instructions?.map((s, i) => <li key={i}>{s}</li>)}</ol>
        </details>
      </div>

      {rest > 0 && (
        <div className="px-4 py-2 bg-brand/15 flex items-center justify-center gap-2" data-testid="rest-timer">
          <Timer className="w-4 h-4 text-brand" /><span className="font-num text-brand font-semibold">Rest: {rest}s</span>
          <button onClick={() => setRest(0)} className="text-xs text-secondary underline ml-2">skip</button>
        </div>
      )}

      <div className="p-4 border-t border-border flex items-center gap-2">
        <button data-testid="player-prev" disabled={idx === 0} onClick={() => setIdx(idx - 1)} className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center disabled:opacity-30"><ChevronLeft className="w-5 h-5" /></button>
        <button data-testid="exercise-done" onClick={markDone}
          className={`flex-1 h-12 rounded-xl font-bold uppercase tracking-wide flex items-center justify-center gap-2 transition-all ${log.done ? "bg-success/20 text-success" : "bg-brand text-white glow-red hover:bg-brand-hover"}`}>
          {log.done ? <><Check className="w-5 h-5" /> Done</> : <><Check className="w-5 h-5" /> Mark Complete</>}
        </button>
        {idx < exercises.length - 1 ? (
          <button data-testid="player-next" onClick={() => setIdx(idx + 1)} className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center"><ChevronRight className="w-5 h-5" /></button>
        ) : (
          <button data-testid="player-finish" onClick={() => setShowComplete(true)} className="px-4 h-12 rounded-xl bg-success text-white font-bold flex items-center justify-center"><Trophy className="w-5 h-5" /></button>
        )}
      </div>
    </div>
  );
}

function Rating({ label, value, onChange, testId }) {
  return (
    <div>
      <p className="label-xs mb-2">{label}</p>
      <div className="flex gap-2" data-testid={testId}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} onClick={() => onChange(n)} className={`flex-1 h-11 rounded-lg font-num font-semibold border transition-all ${value >= n ? "border-brand bg-brand/15 text-brand" : "border-border text-tertiary"}`}>{n}</button>
        ))}
      </div>
    </div>
  );
}
